import { computed, ref } from 'vue'
import { WidgetExtended, WidgetList } from '@/types/dashboard'
import cloneDeep from 'lodash/cloneDeep'
import { cloneWidgetModel, removeWidgetParameterValue } from '@/components/RedesignComponents/Widgets/widget.helper'
import useConfirmModal from '@/composables/useConfirmModal'
import i18n from '@/i18n'
import { selectedAccountID } from '@/composables/globalData/useEntitiesData'
import { isAiSummaryWidget } from '@/helpers/widgetUtils'
import { WidgetApi } from '@/api/widgetApi'
import { hasConfigChanged } from '@/helpers/widgetConfigUtils'

/* Types */
export type TWidgetModalOptions = {
    widgetData: WidgetList
    onSave?: WidgetModalCallbackType
    onCancel?: WidgetModalCancelCallbackType
}
export type WidgetModalCallbackType = (widget: WidgetList) => void
export type WidgetModalCancelCallbackType = () => void

/* Data */
export const _modalVisibleState = ref(false)
export const widgetModelData = ref<WidgetExtended | undefined>(undefined)
export let widgetOriginalModelData: WidgetExtended | undefined

/* Local Data */
const saveCallbackFn = ref<WidgetModalCallbackType | undefined>(undefined)
const cancelCallbackFn = ref<WidgetModalCancelCallbackType | undefined>(undefined)

/* Computed */
export const editableWidgetId = computed(() => {
    return _modalVisibleState.value && widgetModelData.value?.WidgetID
})
export const ModalVisibleState = computed(() => {
    return _modalVisibleState.value
})

/* Methods */
export function openWidgetEditModal (modalOptions: TWidgetModalOptions) {
    widgetModelData.value = cloneWidgetModel(modalOptions.widgetData)
    cancelCallbackFn.value = modalOptions.onCancel
    saveCallbackFn.value = modalOptions.onSave
    _modalVisibleState.value = true
    cloneOriginalData()
}

export function onSaveWidgetEditModal () {
    const _data = getModalEditableData()
    if (_data && saveCallbackFn.value) {
        saveCallbackFn.value(_data)

        // Trigger force update for AI summary widgets only if period or prompts changed
        if (isAiSummaryWidget(_data) && _data.WidgetID) {
            const shouldForceUpdate = hasConfigChanged(widgetOriginalModelData, widgetModelData.value)
            if (shouldForceUpdate) {
                triggerAiSummaryForceUpdate(_data.WidgetID)
            }
        }
    }
    _modalVisibleState.value = false
    widgetModelData.value = undefined
}

/**
 * Trigger force update API call for AI summary widgets
 * The widget component will detect the props change and refetch automatically
 * @param widgetId - The ID of the widget to force update
 */
async function triggerAiSummaryForceUpdate (widgetId: number) {
    try {
        // Call the API to trigger server-side summary regeneration
        await WidgetApi.forceUpdate(widgetId)
    } catch (error) {
        console.error('Failed to trigger force update for AI summary widget:', error)
    }
}

export async function closeWidgetEditModal () {
    const isConfirm = await beforeCloseValidation()
    if (!isConfirm) {
        return
    }
    if (cancelCallbackFn.value) {
        cancelCallbackFn.value()
    }
    _modalVisibleState.value = false
    widgetModelData.value = undefined
    widgetOriginalModelData = undefined
}

// function isOtherFilter (config: WidgetConfig) {
//     return ![
//         ...ENUM.AUTO_COMPLETE_TYPE_KEYS,
//         ...ENUM.MULTISELECT_TYPE_KEYS
//     ].includes(config.ParameterType || -1)
// }

export function getModalEditableData () {
    const model = cloneDeep(widgetModelData.value)
    // console.log('selectedAccountID.value', selectedAccountID.value)
    try {
        model?.WidgetConfig.forEach(config => {
            removeWidgetParameterValue(config)
            // TODO: need to clarify
            // if (isOtherFilter(config)) {
            //     config.WidgetParameterValueJson = undefined
            //     return
            // }
            // if ()
            if (typeof config.WidgetParameterValue === 'object') {
                config.WidgetParameterValue['AccountList'] = [ selectedAccountID.value ]
                config.WidgetParameterValue = JSON.stringify(config.WidgetParameterValue)
            }

            if (typeof config.WidgetParameterValueJson !== 'object') {
                return
            }

            if (config.WidgetParameterValueJson['EntityPositive']?.length) {
                config.WidgetParameterValueJson['AccountList'] = selectedAccountID.value ? [ selectedAccountID.value ] : []
            } else {
                config.WidgetParameterValueJson['AccountList'] = []
            }
        })
    } catch (e) {
        console.error(e)
    }
    return model
}

async function beforeCloseValidation () {
    const isEqual = JSON.stringify(widgetOriginalModelData) === JSON.stringify(widgetModelData.value)
    if (isEqual) {
        return Promise.resolve(true)
    }
    return await useConfirmModal({
        type: 'confirm',
        message: String(i18n.t('dialog.confirm.update.cancel'))
    })
}

function cloneOriginalData () {
    setTimeout(() => {
        widgetOriginalModelData = cloneDeep(widgetModelData.value)
    }, 1500)
}
